/**
 * Respaldo de la configuración del tótem (cliente + instancia) fuera de localStorage.
 *
 * La configuración vive en localStorage (`kiosk_config`), que el navegador puede borrar: al liberar
 * espacio, al reinstalar la app web o tras semanas sin uso. Si eso pasa, el tótem vuelve al asistente.
 * Para evitarlo:
 *   - se pide almacenamiento persistente (`navigator.storage.persist()`), que el navegador no libera
 *     por su cuenta;
 *   - se guarda una copia en IndexedDB y, si localStorage llega vacío, se recupera de ahí.
 * Todo es "mejor esfuerzo": si el navegador no soporta algo o falla, el tótem sigue como antes.
 */

const DB_NAME = "sok";
const STORE = "config";
const KEY = "kiosk_config";
// Si IndexedDB no responde, no se deja el tótem esperando: se sigue como si no hubiera respaldo.
const RESTORE_TIMEOUT_MS = 2000;

function openDb() {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("indexeddb_unavailable"));
    const request = indexedDB.open(DB_NAME, 1);
    request.onupgradeneeded = () => request.result.createObjectStore(STORE);
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

async function run(mode, action) {
  const db = await openDb();
  try {
    return await new Promise((resolve, reject) => {
      const tx = db.transaction(STORE, mode);
      const request = action(tx.objectStore(STORE));
      tx.oncomplete = () => resolve(request.result);
      tx.onerror = () => reject(tx.error);
      tx.onabort = () => reject(tx.error);
    });
  } finally {
    db.close();
  }
}

/** Pide que el navegador no borre los datos del sitio por su cuenta. */
export async function requestPersistentStorage() {
  try {
    if (!navigator.storage?.persist) return false;
    if (await navigator.storage.persisted?.()) return true;
    return await navigator.storage.persist();
  } catch (error) {
    console.error("[Config] Error requesting persistent storage:", error);
    return false;
  }
}

/** Guarda la copia de la configuración. */
export async function backupConfig(config) {
  try {
    await run("readwrite", (store) => store.put(JSON.stringify(config), KEY));
    return true;
  } catch (error) {
    console.error("[Config] Error backing up config:", error);
    return false;
  }
}

/** Borra la copia (al cambiar la configuración desde el menú secreto). */
export async function clearConfigBackup() {
  try {
    await run("readwrite", (store) => store.delete(KEY));
    return true;
  } catch (error) {
    console.error("[Config] Error clearing config backup:", error);
    return false;
  }
}

/** Devuelve la configuración respaldada, o null si no hay, no es válida o IndexedDB no responde. */
export async function restoreConfigBackup() {
  try {
    const raw = await Promise.race([
      run("readonly", (store) => store.get(KEY)),
      new Promise((resolve) => setTimeout(() => resolve(null), RESTORE_TIMEOUT_MS)),
    ]);
    return parseConfig(raw);
  } catch (error) {
    console.error("[Config] Error restoring config backup:", error);
    return null;
  }
}

/** Una configuración sirve solo si trae cliente e instancia. */
export function parseConfig(raw) {
  if (typeof raw !== "string" || !raw) return null;
  try {
    const config = JSON.parse(raw);
    return config && typeof config.client === "string" && config.client && typeof config.instance === "string" && config.instance ? config : null;
  } catch {
    return null;
  }
}
